import type { StatusLogFiltersInput } from 'types/graphql'

import CohortRetentionTable from './CohortRetentionTable'
import RetentionByJobListingCard from './RetentionByJobListingCard'
import RetentionByLeadSourceCard from './RetentionByLeadSourceCard'
import RetentionByRecruiterCard from './RetentionByRecruiterCard'
import RetentionCurveCard from './RetentionCurveCard'
import TerminationReasonsCard from './TerminationReasonsCard'

export interface RetentionTabProps {
  filters: StatusLogFiltersInput
  /**
   * Bumped by the page-level Refresh button. Each self-fetching card watches
   * this and calls its own `refetch()` when the value changes — keeps the
   * Refresh button in sync with the Retention tab without lifting queries up.
   */
  refreshKey: number
}

/**
 * Composed Retention tab for AnalyticsPageV2. Reads the same filter shape that
 * the hero KPIs use, plumbs it down to six self-fetching cards.
 *
 * Layout (top to bottom):
 *   1. Retention curve — survival chart with day-30/60/90 markers (full width)
 *   2. Cohort retention table — monthly cohorts × 30/60/90-day retention
 *   3. Retention by job listing | Retention by recruiter (2-col grid)
 *   4. Retention by lead source | Termination reasons (2-col grid)
 *
 * See `playground/retention-tab.html` for the visual reference.
 */
const RetentionTab = ({ filters, refreshKey }: RetentionTabProps) => {
  return (
    <div className="flex flex-col gap-4">
      <RetentionCurveCard filters={filters} refreshKey={refreshKey} />
      <CohortRetentionTable filters={filters} refreshKey={refreshKey} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <RetentionByJobListingCard filters={filters} refreshKey={refreshKey} />
        <RetentionByRecruiterCard filters={filters} refreshKey={refreshKey} />
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <RetentionByLeadSourceCard filters={filters} refreshKey={refreshKey} />
        <TerminationReasonsCard filters={filters} refreshKey={refreshKey} />
      </div>
    </div>
  )
}

export default RetentionTab
